import { useState } from 'react';

import { toastService } from '@thefarmersfront/kitchen';

import { registerMockAPI } from '~/mocks/mockApis';

import { useAPI, useAsyncHandler } from '~/hooks/common';

import { API_ENDPOINT, TOAST_MESSAGE } from '~/constants';

import type { TypeErrorResponseData } from '~/types/common.types';
import type {
  TypeGetVendorReleaseDetailItemsParams,
  TypeGetVendorReleaseDetailItemsResponse,
  TypeGetVendorReleaseDetailResponse,
  TypeStockReleaseDetailItem,
} from '~/types/stock.types';

/**
 * [재고관리] 반출 요청 상세 api 요청 훅
 */
const useGetVendorReleaseDetail = () => {
  const { runAsync } = useAsyncHandler();

  const [responseData, setResponseData] = useState<TypeGetVendorReleaseDetailResponse['data'] | null>(null);
  const [responseItemsData, setResponseItemsData] = useState<TypeGetVendorReleaseDetailItemsResponse | null>(null);

  /**
   * 반출 요청 상세 정보 조회
   */
  const getVendorReleaseId = useAPI<unknown, TypeGetVendorReleaseDetailResponse>({
    endpoint: API_ENDPOINT.STOCK.GET_VENDOR_RELEASE_ID,
    method: 'get',
  });

  const requestVendorReleaseDetail = async (
    id: number,
    {
      onSuccess,
      onError,
    }: {
      onSuccess?: (data: TypeGetVendorReleaseDetailResponse['data']) => void;
      onError?: (error: TypeErrorResponseData['error']) => void;
    } = {}
  ) => {
    runAsync(() => getVendorReleaseId({ pathParams: `/${id}` }), {
      onSuccess: ({ data }) => {
        setResponseData(data);
        onSuccess?.(data);
      },
      onError: (error: TypeErrorResponseData['error']) => {
        toastService.addToast({
          type: 'error',
          message: error.message ?? TOAST_MESSAGE.DEFAULT_ERROR,
        });
        onError?.(error);
      },
    });
  };

  /**
   * 반출 요청 상세 상품 목록 조회
   */
  const getVendorReleaseIdItems = useAPI<
    TypeGetVendorReleaseDetailItemsParams,
    TypeGetVendorReleaseDetailItemsResponse
  >({
    endpoint: API_ENDPOINT.STOCK.GET_VENDOR_RELEASE_ID,
    method: 'get',
  });

  const requestVendorReleaseDetailItems = async (
    { id, ...params }: { id: number } & TypeGetVendorReleaseDetailItemsParams,
    {
      onSuccess,
      onError,
    }: {
      onSuccess?: (data: TypeGetVendorReleaseDetailItemsResponse) => void;
      onError?: (error: TypeErrorResponseData['error']) => void;
    } = {}
  ) => {
    runAsync(() => getVendorReleaseIdItems({ pathParams: `/${id}/items`, params }), {
      onSuccess: (res) => {
        setResponseItemsData(res);
        onSuccess?.(res);
      },
      onError: (error: TypeErrorResponseData['error']) => {
        toastService.addToast({
          type: 'error',
          message: error.message ?? TOAST_MESSAGE.DEFAULT_ERROR,
        });
        onError?.(error);
      },
    });
  };

  return { requestVendorReleaseDetail, responseData, requestVendorReleaseDetailItems, responseItemsData };
};

export default useGetVendorReleaseDetail;

const vendorReleaseDetailResponse: TypeGetVendorReleaseDetailResponse = {
  data: {
    detailInfo: {
      vendorReleaseId: 5001,
      status: 'PROGRESS',
      statusCodeObject: { code: 'PROGRESS', name: '진행' },
      clusterCode: 'CC02',
      clusterCodeObject: { code: 'CC02', name: '김포' },
      method: 'PICKUP',
      methodCodeObject: { code: 'PICKUP', name: '방문수령' },
      packingMethod: 'BOX',
      packingMethodCodeObject: { code: 'BOX', name: '박스' },
      expectedDate: new Date('2026-05-21T00:00:00+09:00'),
      createdAt: '2026-05-18T10:17:33',
    },
    destinationAddress: {
      phoneNumber: '01012345678',
      zipCode: '10048',
      address: '경기도 김포시 고촌읍 아라육로 152번길 100',
      detailAddress: '컬리 김포물류센터 1층',
    },
    centerList: [
      {
        vendorReleaseCenterId: 1,
        temperature: 'COLD',
        temperatureCodeObject: { code: 'COLD', name: '냉장' },
        phoneNumber: '0319876543',
        zipCode: '10048',
        address: '경기도 김포시 고촌읍 아라육로 152번길 100',
        detailAddress: '냉장동 출고장',
      },
      {
        vendorReleaseCenterId: 2,
        temperature: 'ROOM',
        temperatureCodeObject: { code: 'ROOM', name: '상온' },
        phoneNumber: '0319876544',
        zipCode: '10048',
        address: '경기도 김포시 고촌읍 아라육로 152번길 100',
        detailAddress: '상온동 출고장',
      },
    ],
    palletInfo: null,
  },
};

const mockDetailItem = (
  index: number,
  overrides: Partial<TypeStockReleaseDetailItem> = {}
): TypeStockReleaseDetailItem => ({
  id: 5001,
  skuCode: `M000005076${String(index).padStart(2, '0')}`,
  barcode: `88123123150${index}`,
  skuName: `(센터명)반출상품${index}`,
  temperatureType: 'COLD',
  temperatureTypeCodeObject: { code: 'COLD', name: '냉장' },
  availableRequestQuantity: 40,
  defectiveRequestQuantity: 0,
  availableActualQuantity: 40,
  defectiveActualQuantity: 0,
  ...overrides,
});

const vendorReleaseDetailItemsResponse: TypeGetVendorReleaseDetailItemsResponse = {
  data: [
    mockDetailItem(1),
    mockDetailItem(2, {
      temperatureType: 'ROOM',
      temperatureTypeCodeObject: { code: 'ROOM', name: '상온' },
      availableRequestQuantity: 1200,
      defectiveRequestQuantity: 30,
      availableActualQuantity: 1000,
      defectiveActualQuantity: 30,
    }),
    mockDetailItem(3, { availableActualQuantity: undefined, defectiveActualQuantity: undefined }),
  ],
  pagination: { listView: 100, listTotal: 3, cursor: 0 },
};

// `/:id` 가 `/recent-info`·`/search` 같은 형제 경로까지 가로채지 않도록 숫자 id 만 받는다.
registerMockAPI<TypeGetVendorReleaseDetailResponse>({
  method: 'get',
  endpoint: `${API_ENDPOINT.STOCK.GET_VENDOR_RELEASE_ID}/:id(\\d+)`,
  label: '재고/반출요청상세/상세정보',
  scenarios: [{ name: '정상 응답 — 출발지 2곳', response: vendorReleaseDetailResponse }],
});

registerMockAPI<TypeGetVendorReleaseDetailItemsResponse>({
  method: 'get',
  endpoint: `${API_ENDPOINT.STOCK.GET_VENDOR_RELEASE_ID}/:id(\\d+)/items`,
  label: '재고/반출요청상세/반출상품',
  scenarios: [{ name: '정상 응답 — 3건', response: vendorReleaseDetailItemsResponse }],
});
